import { ResumeDigitalTwin } from "@/domain/DigitalTwin/DigitalTwin";
import { createContext, Dispatch, SetStateAction, useContext, useMemo, useState } from "react";


type ParameterClass = 'hydrotwins' | 'historical' | 'forecast' | 'calculated' | null;

interface DigitalTwinsContextInterface {
    selectedDigitalTwinId: string | null;
    setSelectedDigitalTwinId: Dispatch<SetStateAction<string | null>>;
    selectedDigitalTwinsData: ResumeDigitalTwin[];
    setSelectedDigitalTwinsData: Dispatch<SetStateAction<ResumeDigitalTwin[]>>;
    hoveredParameterClass: ParameterClass;
    setHoveredParameterClass: Dispatch<SetStateAction<ParameterClass>>;
    lockedParameterClass: ParameterClass;
    setLockedParameterClass: Dispatch<SetStateAction<ParameterClass>>;
    hiddenParameterClasses: Set<ParameterClass>;
    setHiddenParameterClasses: Dispatch<SetStateAction<Set<ParameterClass>>>;
    showGisLayer: boolean;
    setShowGisLayer: Dispatch<SetStateAction<boolean>>;
}

export type { ParameterClass };

const DigitalTwinsContext = createContext<DigitalTwinsContextInterface | undefined>(undefined);

export const useDigitalTwinsContext = () => {
    const context = useContext(DigitalTwinsContext);
    if (!context) {
        throw new Error('useDigitalTwinsContext must be used within a DigitalTwinsContextProvider');
    }
    return context;
};

export const DigitalTwinsContextProvider = ({ children, initialValue = {  selectedDigitalTwinId: null, selectedDigitalTwinsData: [] } }: { children: React.ReactNode, initialValue?: Partial<Pick<DigitalTwinsContextInterface, 'selectedDigitalTwinId' | 'selectedDigitalTwinsData'>> }) => {
    const [selectedDigitalTwinId, setSelectedDigitalTwinId] = useState<string | null>(initialValue.selectedDigitalTwinId || null);
    const [selectedDigitalTwinsData, setSelectedDigitalTwinsData] = useState<ResumeDigitalTwin[]>(initialValue.selectedDigitalTwinsData || []);
    const [hoveredParameterClass, setHoveredParameterClass] = useState<ParameterClass>(null);
    const [lockedParameterClass, setLockedParameterClass] = useState<ParameterClass>(null);
    const [hiddenParameterClasses, setHiddenParameterClasses] = useState<Set<ParameterClass>>(new Set());
    const [showGisLayer, setShowGisLayer] = useState<boolean>(true);
    
    const contextValue = useMemo(() => ({
        selectedDigitalTwinId,
        setSelectedDigitalTwinId,
        selectedDigitalTwinsData,
        setSelectedDigitalTwinsData,
        hoveredParameterClass,
        setHoveredParameterClass,
        lockedParameterClass,
        setLockedParameterClass,
        hiddenParameterClasses,
        setHiddenParameterClasses,
        showGisLayer,
        setShowGisLayer,
    }), [selectedDigitalTwinId, selectedDigitalTwinsData, hoveredParameterClass, lockedParameterClass, hiddenParameterClasses, showGisLayer]);

    return (
        <DigitalTwinsContext.Provider value={contextValue}>
            {children}
        </DigitalTwinsContext.Provider>
    );
};
